import React from 'react';
import { useProject } from './queries';
import ProjectNotFound from 'projects/components/ProjectNotFound';

/**
 * Custom hook that wraps useProject and handles 404 errors
 * @param {string|number} projectId - The project ID to fetch
 * @returns {Object} Object with project data, error, loading state, and NotFoundComponent
 */
export function useProjectWithErrorHandling(projectId) {
  const queryResult = useProject(projectId);
  const { error: projectError } = queryResult;
  
  // Check for 404 or 403 in multiple ways since the error might be wrapped by API layer
  const is404or403 = projectError && (
    projectError.status === 404 || 
    projectError.status === 403 ||
    projectError.response?.status === 404 ||
    projectError.response?.status === 403 ||
    projectError.originalError?.response?.status === 404 ||
    projectError.originalError?.response?.status === 403 ||
    projectError.name === 'HTTP404Error' ||
    projectError.name === 'HTTP403Error'
  );
  
  // Component to render if project not found or forbidden
  const NotFoundComponent = is404or403 ? ProjectNotFound : null;
  
  return {
    ...queryResult,
    is404: is404or403,
    hasError: !!projectError,
    NotFoundComponent
  };
}

/**
 * HOC that automatically renders ProjectNotFound on 404
 * Use this to wrap components that should show ProjectNotFound
 */
export function withProjectErrorBoundary(Component) {
  return function ProjectErrorBoundary(props) {
    const { NotFoundComponent, ...projectData } = useProjectWithErrorHandling(props.projectID);
    
    if (NotFoundComponent) {
      return <NotFoundComponent />;
    }
    
    return <Component {...props} projectData={projectData} />;
  };
}